import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { loanProducts } from '@/mocks/home';

export default function ProductsSection() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const checkScroll = () => {
    if (!scrollRef.current) return;
    const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
    setCanScrollLeft(scrollLeft > 0);
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
  };

  const scroll = (direction: 'left' | 'right') => {
    if (!scrollRef.current) return;
    const amount = 320;
    scrollRef.current.scrollBy({ left: direction === 'left' ? -amount : amount, behavior: 'smooth' });
    setTimeout(checkScroll, 300);
  };

  return (
    <section id="products" className="w-full py-16 md:py-24 bg-background-100">
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="flex items-end justify-between mb-10 md:mb-14">
          <div>
            <p className="text-sm font-label font-semibold text-primary-500 mb-2 tracking-wider uppercase">
              Loan Products
            </p>
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900">
              대표님께 꼭 맞는{' '}
              <span className="text-primary-500">대출상품</span>
            </h2>
            <p className="mt-3 text-sm md:text-base text-foreground-600 font-label max-w-lg">
              사업 유형과 규모에 맞춰 최적의 대출 상품을 추천해 드립니다.
            </p>
          </div>
          <div className="hidden md:flex gap-2">
            <button
              onClick={() => scroll('left')}
              className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${
                canScrollLeft
                  ? 'bg-primary-500 text-white hover:bg-primary-600'
                  : 'bg-background-200 text-foreground-400 cursor-not-allowed'
              }`}
              aria-label="이전"
            >
              <i className="ri-arrow-left-line w-5 h-5 flex items-center justify-center" />
            </button>
            <button
              onClick={() => scroll('right')}
              className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${
                canScrollRight
                  ? 'bg-primary-500 text-white hover:bg-primary-600'
                  : 'bg-background-200 text-foreground-400 cursor-not-allowed'
              }`}
              aria-label="다음"
            >
              <i className="ri-arrow-right-line w-5 h-5 flex items-center justify-center" />
            </button>
          </div>
        </div>

        <div
          ref={scrollRef}
          onScroll={checkScroll}
          className="flex gap-4 md:gap-6 overflow-x-auto pb-4 snap-x snap-mandatory scrollbar-hide"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {loanProducts.map((product) => (
            <div
              key={product.id}
              onClick={() => navigate(`/${product.slug}`)}
              className="flex-shrink-0 w-[280px] md:w-[340px] snap-start bg-white rounded-2xl md:rounded-3xl overflow-hidden border border-background-200/70 hover:border-primary-300 transition-all duration-300 hover:shadow-lg group cursor-pointer"
            >
              <div className="relative h-44 md:h-52 overflow-hidden">
                <img
                  src={product.image}
                  alt={product.title}
                  className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-primary-900/20" />
                <span className="absolute top-3 left-3 px-3 py-1 rounded-full bg-primary-500 text-white text-xs font-label font-semibold">
                  {product.maxAmount}
                </span>
              </div>
              <div className="p-5 md:p-6">
                <h3 className="font-heading text-lg md:text-xl font-bold text-foreground-900 mb-1">
                  {product.title}
                </h3>
                <p className="text-sm text-foreground-600 font-label mb-4">{product.subtitle}</p>
                <div className="flex flex-wrap gap-2 mb-4">
                  {product.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-2.5 py-1 rounded-full bg-primary-50 text-primary-700 text-xs font-label font-medium"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
                <span className="inline-flex items-center gap-1 text-sm font-label font-semibold text-primary-600 group-hover:text-primary-700 transition-colors">
                  자세히 보기
                  <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center" />
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}